import Vue from 'vue'
import { MARKET_TYPE } from 'const/stock'

/**
 * 股票搜索（国际）
 * @param {object} options
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchSearchStock(options) {
  options.params = {
    withCnEtf: false,
    withPlate: false,
    ...options.params
  }
  return Vue.http.get('~stock/suggest/v4/{keyword}', options)
}

/**
 * 获取股票市场指数
 * @param {object} params
 * @param {string} params.market - 股票市场类型
 * @param {boolean} params.configIndices - 是否可配置指数
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchStockMarketIndices(params) {
  return Vue.http.get('~stock/market/{market}/indices', { params })
}
/**
 * 获取全市场股票详情
 * @param {array} items symbol列表
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchGlobalStockDetail(items) {
  return Vue.http.post('~stock/stock_info/detail/global', { items })
}

/**
 * 获取股票信息详情
 * @param {array} items symbol列表
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchStockInfoDetails(market, items) {
  if (market === MARKET_TYPE.HK) {
    return Vue.http.post('~stock/hkstock/stock_info/detail', { items })
  }
  if (market === MARKET_TYPE.US) {
    return Vue.http.post('~stock/stock_info/detail', { items })
  }
}

/**
 * 获取一支股票的股票详情
 * @param {string} symbol
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchOneStockInfoDetail(symbol) {
  return Vue.http.get(`~stock/stock_info/detail/${symbol}`)
}

/**
 * 获取自选股列表
 * @param {object} params
 * @param {Object} options
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchWatchList(params, options) {
  return Vue.http.get('~stock/watchlist/get', { ...options, params })
}
/**
 * 添加自选列表
 * @param {array} items
 * @returns {PromiseLike<HttpResponse>}
 */
export function reqAddWatchList(items) {
  return Vue.http.post('~stock/watchlist/add', { items })
}
/**
 * 更新自选列表
 * @param {array} items
 * @returns {PromiseLike<HttpResponse>}
 */
export function reqUpdateWatchList(items) {
  return Vue.http.post('~stock/watchlist/update', { items })
}
/**
 * 删除自选列表
 * @param {array} items
 * @returns {PromiseLike<HttpResponse>}
 */
export function reqRemoveWatchList(items) {
  return Vue.http.post('~stock/watchlist/remove', { items })
}
/**
 * 添加自定义自选列表分组
 * @param {array<string>} items
 * @returns {PromiseLike<HttpResponse>}
 */
export function reqAddWatchListGroup(items) {
  return Vue.http.post('~stock/watchlist/group/add', { items })
}
/**
 * 获取自选列表分组
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchWatchListGroup() {
  return Vue.http.get('~stock/watchlist/group/get')
}
/**
 * 设置自选列表分组
 * @param {array<string>} items
 * @returns {PromiseLike<HttpResponse>}
 */
export function reqSetWatchListGroup(items) {
  return Vue.http.post('~stock/watchlist/group/set', { items })
}
/**
 * 更新自选股列表分组
 * @param {array<string>} items
 * @returns {PromiseLike<HttpResponse>}
 */
export function reqUpdateWatchListGroup(items) {
  return Vue.http.post('~stock/watchlist/group/update', { items })
}
/**
 * 移除自定义自选列表分组
 * @param {array<string>} items
 * @returns {PromiseLike<HttpResponse>}
 */
export function reqRemoveWatchListGroup(items) {
  return Vue.http.post('~stock/watchlist/group/remove', { items })
}

/**
 * 获取公司行动日历数据
 * @param {array<string>} items 股票标识列表
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchCorporateActionsCalendar(items) {
  return Vue.http.post('~stock/fundamental/corporate_actions/calendar', {
    items
  })
}

/**
 * 获取全市场股票行情摘要
 * @param {object} data
 * @param {boolean} data.lite 是否只返回交易中的股票信息
 * @param {boolean} data.hkDelay 强制lv0 用户延时
 * @param {boolean} data.usDelay 强制lv0美股用户延时
 * @param {array} data.items 股票代码
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchAllMarketStockBriefs(data) {
  const { items, ...params } = data
  return Vue.http({
    url: '~stock/stock_info/brief/all',
    method: 'POST',
    params: params,
    body: { items }
  })
}

/**
 * 获取 A 股买卖盘信息
 * @param {string} symbol 股票代码
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchAStockAskBid(symbol) {
  return Vue.http.get(`~stock/astock/stock_info/ask_bid/${symbol}`)
}
/**
 * 获取港股买卖盘信息
 * @param {string} symbol 股票代码
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchHkStockAskBid(symbol) {
  return Vue.http.get(`~stock/hkstock/stock_info/ask_bid/${symbol}`)
}
/**
 * 获取美股lv2买卖盘
 * @param {string} params.symbol 股票代码
 * @param {string} params.props 希望返回的数据（逗号分隔的字符串）：askBidDepth / askBidHist / askBidBig
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchUsLv2AskBid(params) {
  return Vue.http
    .get('~stock/stock_info/ask_bid_all/{symbol}', {
      params
    })
    .then(resp => resp.body)
}

/**
 * 获取个股企业行动数据
 * @param {string} params.market 市场
 * @param {string} params.symbol 股票代码
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchStockCorporateActions(params) {
  return Vue.http.get(
    '~stock/fundamental/corporate_actions/{market}/{symbol}',
    { params }
  )
}

/**
 * 获取公司行动列表
 * @param items
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchStockCorActionList(items) {
  return Vue.http
    .post('~stock/fundamental/corporate_actions/calendar', {
      items
    })
    .then(({ data: respData }) => {
      let items = []
      let corporates = []
      try {
        items = respData.data.items || []
      } catch (e) {
        items = []
      }
      items.forEach(item => {
        corporates = corporates.concat(item.corporates)
      })
      return corporates
    })
}

/**
 * 获取公司行动详情页面数据
 * @param {string} params.symbol - 股票代码
 * @param {number} params.limit - 限制
 * @param {string} [params.order] - 排序
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchStockCorporateActionsDetail(params) {
  return Vue.http.get('~stock/fundamental/corporate_actions/details/{symbol}', {
    params
  })
}
/**
 * 获取 A 股提示信息
 * @param {string} symbol 股票代码
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchAStockNotice(symbol) {
  return Vue.http.get(`~stock/astock/notice/${symbol}`)
}

/**
 * 获取股票统计指数数据
 * @param {string} symbol 股票代码
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchStockFundamentalInfo(symbol) {
  return Vue.http.get(`~stock/stock_info/fundamental/${symbol}`)
}

/**
 * 获取港股股票统计指数数据
 * @param {string} symbol 股票代码
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchHkStockFundamentalInfo(symbol) {
  return Vue.http.get(`~stock/hkstock/stock_info/fundamental/${symbol}`)
}

/**
 * 获取股票逐笔交易信息
 * @param {object} data
 * @param {string} data.market 股票市场
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchStockTradeTick(data) {
  const { market, ...params } = data
  let api = '~stock/stock_info/trade_tick/{symbol}'
  if (market === MARKET_TYPE.HK) {
    api = '~stock/hkstock/stock_info/trade_tick/{symbol}'
  }
  if (MARKET_TYPE.A.includes(market)) {
    api = '~stock/astock/trade_tick/{symbol}'
  }
  return Vue.http.get(api, { params })
}

/**
 * 获取财务详情
 * @params {string} params.type 财务表类型
 * @params {string} params.symbol 股票代码
 * @params {string} params.market 市场
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchFinancialDetail(params) {
  return Vue.http.get('~stock/fundamental/earnings/{type}/{symbol}', { params })
}

/**
 * 个股详情财务报表（三表汇总）
 * @params {string} params.symbol - 股票代码
 * @params {string} params.type - 类型
 * @params {string} params.market - 市场
 * @params {string} [params.period] - 周期
 * @params {string} params.country - 国家
 */
export function fetchStockFinancial(params) {
  return Vue.http.get('~stock/fundamental/publicity/{type}/{symbol}', {
    params
  })
}

/**
 * 获取公司主营业务概览
 * @param {string} params.symbol - 股票代码
 * @param {string} params.market - 股票市场
 */
export function fetchBusinessSegmentSummary(params) {
  return Vue.http.get('~stock/fundamental/segment/summary', { params })
}

/**
 * 获取A股财务信息
 * @param {string} params.type 表格数据类型
 * @param {string} params.version 版本
 * @param {string} params.symbol 股票代码
 */
export function fetchAStockFinancial(params) {
  return Vue.http.get('~stock/fundamental/astock/symbol/publicity/{symbol}', {
    params
  })
}

/**
 * 获取港股期权链
 * @params {object} params
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchHkStockWarrantChain(params) {
  return Vue.http.get('~stock/hkstock/warrant/chain/{symbol}', { params })
}

/**
 * 获取市场主题
 * @param {string} [order] - 排序
 * @param {string} market - 市场
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchStockMarketTopics(market, order) {
  const apis = {
    [MARKET_TYPE.US]: '~stock/market/topics',
    [MARKET_TYPE.HK]: '~stock/hkstock/market/topics',
    [MARKET_TYPE.SH]: '~stock/astock/market/topics'
  }
  return Vue.http.get(apis[market || MARKET_TYPE.US], { params: { order } })
}
/**
 * 获取某个主题股票市场行情
 * @param {object} data
 * @param {string} data.market 市场
 * @param {object} options 请求参数
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchStockMarketQuote(data, options) {
  const { market, ...params } = data
  let api = '~stock/market/quote/{topicId}'

  if (market === MARKET_TYPE.HK) {
    api = '~stock/hkstock/market/quote/{topicId}'
  }
  if (MARKET_TYPE.A.includes(market)) {
    api = '~stock/astock/market/quote/{topicId}'
  }

  return Vue.http.get(api, { params, ...options })
}

/**
 * 获取股票市场消息提醒
 * @param {string} region - 国家地区
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchStockMarketNotice(region) {
  return Vue.http.get('~stock/market/notice', { params: { region } })
}

/**
 * 按照时间段获取投资日历的分类数据
 * @param {string} startDate - 开始时间
 * @param {string} endDate - 结束时间
 * @param {object} options - 请求参数
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchStockFinanceCalendarClassify(startDate, endDate, options) {
  return Vue.http.get('~stock/fundamental/finance_calendar/origin/class', {
    params: { startDate, endDate },
    ...options
  })
}

/**
 * 按日期获取投资日历数据
 * @param {string} date - 日期
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchStockFinanceCalendarData(date) {
  return Vue.http.get('~stock/fundamental/finance_calendar/origin', {
    params: { date }
  })
}

/**
 * 获取股票的分价数据
 * @param { string } market - 股票市场。支持港股和美股
 * @param { string } symbol - 股票代码
 * @param { number = 0 } [page] - 页码
 * @param { number = 50 } [size] - 每页数据。-1表示全部数据
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchStockTradePrices(market, symbol, size, page = 0) {
  const params = {
    symbol,
    size,
    page
  }
  const isHK = market === MARKET_TYPE.HK
  const url = isHK
    ? '/hkstock/stock_info/trade_price_list/{symbol}'
    : '/stock_info/trade_price_list/{symbol}'
  return Vue.http.get(url, { params })
}

/**
 * 获取市场热力图数据
 * @param {string} params.index - 市场指数
 * @param {string} params.period - 时间维度
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchStockHeatMapDetail(params) {
  return Vue.http.get('~stock/market/heatmap/{index}/{period}', { params })
}

/**
 * 获取 US NBBO 行情数据
 * @param {string} params.symbol - 股票代码
 */
export function fetchNBBOMarketData(symbol) {
  return Vue.http
    .get('~stock/api/stock/us/nbbo/price', { params: { symbol } })
    .then(res => {
      if (res.data.ret === 0) {
        return res.data.data
      }
      throw res.data
    })
}
